import { useRef, useCallback, type ReactElement } from 'react';
import type { RefObject } from 'react';
import type { GalleryImage, TimelineGroup, TimelineGroupBy } from '../../types/gallery';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { useTranslation } from '../../i18n/useTranslation';
import './GalleryTimeline.css';

export interface GalleryTimelineProps {
  images: GalleryImage[];
  groupBy: TimelineGroupBy;
  onImageClick: (index: number) => void;
  loadMoreRef?: RefObject<HTMLDivElement>;
  hasMore?: boolean;
  isLoading?: boolean;
}

function getISOWeekMonday(date: Date): Date {
  const d = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()));
  const day = d.getUTCDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setUTCDate(d.getUTCDate() + diff);
  return d;
}

export interface GroupLabelOptions {
  /** BCP 47 language for dates (default: the browser's). */
  language?: string;
  /** Label of a week group from its formatted start and end dates. */
  weekLabel?: (start: string, end: string) => string;
}

const defaultWeekLabel = (start: string, end: string): string => `Week of ${start} – ${end}`;

function formatGroupLabel(key: string, groupBy: TimelineGroupBy, options: GroupLabelOptions = {}): string {
  const language = options.language || undefined;
  if (groupBy === 'day') {
    const d = new Date(key + 'T00:00:00');
    return d.toLocaleDateString(language, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  }
  if (groupBy === 'week') {
    const monday = new Date(key + 'T00:00:00');
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const start = monday.toLocaleDateString(language, { month: 'short', day: 'numeric' });
    const end = sunday.toLocaleDateString(language, { month: 'short', day: 'numeric', year: 'numeric' });
    return (options.weekLabel ?? defaultWeekLabel)(start, end);
  }
  if (groupBy === 'month') {
    const d = new Date(key + '-01T00:00:00');
    return d.toLocaleDateString(language, { year: 'numeric', month: 'long' });
  }
  // year
  return key;
}

function getGroupKey(date: Date, groupBy: TimelineGroupBy): string {
  if (groupBy === 'day') {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    const d = String(date.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (groupBy === 'week') {
    const monday = getISOWeekMonday(date);
    const y = monday.getUTCFullYear();
    const m = String(monday.getUTCMonth() + 1).padStart(2, '0');
    const d = String(monday.getUTCDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  if (groupBy === 'month') {
    const y = date.getUTCFullYear();
    const m = String(date.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }
  // year
  return String(date.getUTCFullYear());
}

export function groupImagesByPeriod(
  images: GalleryImage[],
  groupBy: TimelineGroupBy,
  labelOptions: GroupLabelOptions = {},
): TimelineGroup[] {
  const map = new Map<string, GalleryImage[]>();

  for (const image of images) {
    const date = new Date(image.uploadedAt);
    const key = getGroupKey(date, groupBy);
    const existing = map.get(key);
    if (existing) {
      existing.push(image);
    } else {
      map.set(key, [image]);
    }
  }

  const sorted = Array.from(map.entries()).sort((a, b) => b[0].localeCompare(a[0]));

  return sorted.map(([key, groupImages]) => ({
    key,
    label: formatGroupLabel(key, groupBy, labelOptions),
    images: groupImages,
  }));
}

function formatUploadDate(uploadedAt: string, language?: string): string {
  const d = new Date(uploadedAt);
  return d.toLocaleDateString(language || undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function GalleryTimeline({
  images,
  groupBy,
  onImageClick,
  loadMoreRef,
  hasMore = false,
  isLoading = false,
}: GalleryTimelineProps): ReactElement {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const groups = groupImagesByPeriod(images, groupBy, {
    language,
    weekLabel: (start, end) => t('gallery.timeline.weekOf', { start, end }),
  });

  // Build index map from original input array order so callers receive
  // the index of each image in the original images[] array, not sorted order.
  const indexMapRef = useRef<Map<string, number>>(new Map());
  const newMap = new Map<string, number>();
  images.forEach((img, idx) => {
    newMap.set(img.id, idx);
  });
  indexMapRef.current = newMap;

  const handleImageClick = useCallback(
    (imageId: string) => {
      const idx = indexMapRef.current.get(imageId);
      if (idx !== undefined) {
        onImageClick(idx);
      }
    },
    [onImageClick],
  );

  if (images.length === 0 && !isLoading) {
    return (
      <div className="gallery-timeline">
        <Text className="gallery-timeline__empty">{t('gallery.grid.empty')}</Text>
      </div>
    );
  }

  return (
    <div className="gallery-timeline">
      {groups.map((group) => (
        <section key={group.key} className="gallery-timeline__group" aria-labelledby={`timeline-group-${group.key}`}>
          <Heading level={2} id={`timeline-group-${group.key}`} className="gallery-timeline__group-header">
            <span className="gallery-timeline__group-label">{group.label}</span>
            <span className="gallery-timeline__group-count">
              {t('gallery.imageCount', { n: group.images.length })}
            </span>
          </Heading>
          <div className="gallery-timeline__grid" role="list">
            {group.images.map((image) => {
              const imgIndex = indexMapRef.current.get(image.id) ?? 0;
              return (
                <div key={image.id} role="listitem" className="gallery-timeline__cell">
                <div
                  className="gallery-timeline__tile"
                  role="button"
                  onClick={() => handleImageClick(image.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleImageClick(image.id);
                    }
                  }}
                  tabIndex={0}
                  aria-label={
                    image.hasAiAnalysis === true
                      ? t('gallery.timeline.imageLabelAi', { n: imgIndex + 1, date: formatUploadDate(image.uploadedAt, language) })
                      : t('gallery.timeline.imageLabel', { n: imgIndex + 1, date: formatUploadDate(image.uploadedAt, language) })
                  }
                >
                  <div className="gallery-timeline__tile-image-wrapper">
                    <img
                      src={image.thumbnailUrl ?? image.displayUrl ?? undefined}
                      alt={image.originalFilename || t('gallery.timeline.imageAlt', { date: formatUploadDate(image.uploadedAt, language) })}
                      className="gallery-timeline__tile-image"
                      loading="lazy"
                    />
                    {image.hasAiAnalysis === true && (
                      <span
                        className="gallery-timeline__ai-badge"
                        role="img"
                        title={t('gallery.aiBadgeLabel')}
                        aria-label={t('gallery.aiBadgeLabel')}
                      >
                        {t('gallery.aiBadge')}
                      </span>
                    )}
                  </div>
                  <div className="gallery-timeline__tile-meta">
                    <time className="gallery-timeline__tile-date" dateTime={image.uploadedAt}>
                      {formatUploadDate(image.uploadedAt, language)}
                    </time>
                  </div>
                </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {isLoading && (
        <div className="gallery-timeline__loading">
          <LiveRegion visuallyHidden>{t('gallery.timeline.loadingMore')}</LiveRegion>
          <div className="gallery-timeline__skeleton-group" aria-hidden="true">
            <div className="gallery-timeline__skeleton-header" />
            <div className="gallery-timeline__grid">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="timeline-skeleton gallery-timeline__tile gallery-timeline__tile--skeleton"
                  data-testid="gallery-skeleton"
                >
                  <div className="gallery-timeline__tile-image-wrapper gallery-timeline__tile-image-wrapper--skeleton" />
                  <div className="gallery-timeline__tile-meta gallery-timeline__tile-meta--skeleton" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {hasMore && !isLoading && loadMoreRef && (
        <div ref={loadMoreRef} className="gallery-timeline__load-more-sentinel" aria-hidden="true" />
      )}
    </div>
  );
}

export default GalleryTimeline;
